import { useState } from "react";
import { api, type SandlotCustomerDetail } from "../app/api";
import { StatusChip } from "../components/StatusChip";

function matchTone(status: string): "ok" | "warn" | "no" {
  if (status === "matched") return "ok";
  if (status === "needs_review") return "warn";
  return "no";
}

export function CustomerInspectorPage() {
  const [value, setValue] = useState("hiramrios26@gmail.com");
  const [data, setData] = useState<SandlotCustomerDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function inspect(e: React.FormEvent) {
    e.preventDefault();
    const v = value.trim();
    if (!v) return;
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const params = v.includes("@") ? { email: v } : { id: v };
      setData(await api.sandlotCustomer(params));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }

  const m = data?.match;

  return (
    <>
      <h1>Customer Inspector</h1>
      <p className="page-sub">
        Read-only side-by-side of a Sandlot customer and the matching Shopify
        customer, with the proposed (not applied) migration payload.
      </p>

      <form onSubmit={inspect} style={{ display: "flex", gap: 8, marginBottom: 20 }}>
        <input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="email or Sandlot ID…"
          style={{
            flex: 1,
            padding: "0.5rem 0.7rem",
            background: "var(--surface)",
            border: "1px solid var(--hair)",
            borderRadius: 8,
            color: "var(--ink)",
          }}
        />
        <button
          type="submit"
          style={{
            padding: "0.5rem 1rem",
            background: "var(--accent)",
            color: "#12140f",
            border: "none",
            borderRadius: 8,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          Inspect
        </button>
      </form>

      {loading && <p className="muted">Loading…</p>}
      {error && <p className="muted">{error}</p>}

      {data && !data.found && (
        <div className="banner">
          {data.note}
          {data.shopify.candidates && data.shopify.candidates.length > 0 && (
            <p style={{ marginTop: 8 }}>
              Shopify does have {data.shopify.candidates.length} customer(s) for
              that email: {data.shopify.candidates.map((c) => c.id).join(", ")}
            </p>
          )}
        </div>
      )}

      {data?.found && data.sandlot && (
        <>
          <div className="grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
            <div className="card">
              <h3>Sandlot</h3>
              <table>
                <tbody>
                  <tr><td>ID</td><td className="muted"><code>{data.sandlot.id}</code></td></tr>
                  <tr><td>Name</td><td>{data.sandlot.name ?? "—"}</td></tr>
                  <tr><td>Email</td><td className="muted">{data.sandlot.email ?? "—"}</td></tr>
                  <tr><td>Phone</td><td className="muted">{data.sandlot.phone ?? "—"}</td></tr>
                  <tr><td>Member since</td><td className="muted">{data.sandlot.memberSince ?? "—"}</td></tr>
                  <tr><td>Leader</td><td>{data.sandlot.isLeader ? "Yes" : "No"}</td></tr>
                  <tr><td>Clubs</td><td className="muted">{data.sandlot.clubs.length}</td></tr>
                </tbody>
              </table>
              {data.sandlot.clubs.length > 0 && (
                <ul className="muted" style={{ marginTop: 10, fontSize: "0.85rem" }}>
                  {data.sandlot.clubs.map((cl, i) => (
                    <li key={i}>
                      {cl.officialName ?? cl.groupId}
                      {cl.isLeader === 1 ? " (leader)" : ""}
                    </li>
                  ))}
                </ul>
              )}
              <p className="muted" style={{ fontSize: "0.8rem", marginTop: 8 }}>
                {data.sandlot.eventsNote}
              </p>
            </div>

            <div className="card">
              <h3>Shopify</h3>
              {!data.shopify.configured ? (
                <StatusChip tone="warn" label="Not configured" />
              ) : data.shopify.error ? (
                <p className="muted">{data.shopify.error}</p>
              ) : (
                <>
                  <p className="muted">
                    Candidates: {data.shopify.candidates?.length ?? 0}
                  </p>
                  {m?.shopifyCustomerId ? (
                    <p><code>{m.shopifyCustomerId}</code></p>
                  ) : (
                    <p className="muted">No confident match</p>
                  )}
                  {data.shopify.metafields && data.shopify.metafields.length > 0 && (
                    <>
                      <h3 style={{ marginTop: 14 }}>Existing sandlot metafields</h3>
                      <table>
                        <tbody>
                          {data.shopify.metafields.map((mf) => (
                            <tr key={mf.key}>
                              <td><code>{mf.key}</code></td>
                              <td className="muted">{mf.value ?? "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </>
                  )}
                </>
              )}
            </div>
          </div>

          {m && (
            <div className="card" style={{ marginTop: "1.2rem" }}>
              <h3>Match assessment</h3>
              <p style={{ margin: "0 0 8px" }}>
                <StatusChip tone={matchTone(m.matchStatus)} label={m.matchStatus} />{" "}
                <span className="muted">
                  confidence {m.confidenceScore} · {m.candidateCount} candidate(s) ·
                  reasons: {m.reasons.join(", ") || "none"}
                </span>
              </p>
              {m.conflicts.length > 0 && (
                <p className="muted">Conflicts: {m.conflicts.join("; ")}</p>
              )}
            </div>
          )}

          {data.proposedPayload && (
            <div className="card" style={{ marginTop: "1.2rem" }}>
              <h3>Proposed migration payload (preview — not applied)</h3>
              <pre
                style={{
                  overflowX: "auto",
                  fontSize: "0.8rem",
                  background: "var(--surface-2)",
                  padding: "0.9rem",
                  borderRadius: 8,
                }}
              >
                {JSON.stringify(data.proposedPayload, null, 2)}
              </pre>
            </div>
          )}
        </>
      )}
    </>
  );
}
